import { ModuleService } from 'src/services/backend/ModuleService'
import { StartVirtualCandidateSessionRequest } from 'src/services/backend/VirtualCandidateService'

/** The job a run interviews for. Only the requisition is required; Delivery defaults the role. */
export interface VirtualCandidateJob {
    requisitionId: string
    jobRole: string
}

export const EMPTY_JOB: VirtualCandidateJob = { requisitionId: '', jobRole: '' }
/** GAS's limit for the job role. */
export const MAX_JOB_ROLE_LENGTH = 200

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Why the requisition ID can't be used yet, or undefined when it can. */
export const requisitionProblem = (requisitionId: string): string | undefined => {
    if (!requisitionId.trim()) return 'Enter the requisition ID to interview for.'
    if (!UUID.test(requisitionId.trim())) {
        return 'A requisition ID is a UUID, e.g. 030831c9-8ce5-4e41-b719-49e50cc059e9.'
    }
    return undefined
}

/** Why the job role can't be used, or undefined when it can (blank uses Delivery's default). */
export const jobRoleProblem = (jobRole: string): string | undefined =>
    jobRole.trim().length > MAX_JOB_ROLE_LENGTH
        ? `Keep the job role under ${MAX_JOB_ROLE_LENGTH} characters.`
        : undefined

/**
 * The module template version Delivery can order for this module version. Designer sends each
 * validated draft save to Delivery under that save's own ID (the history's draftVersion, the
 * service's latestDraftVersion), not under the module version ID, so a draft is ordered by its
 * newest save. Designer can't ask whether Delivery has that save: downstream-status reports the
 * module version itself for versions in review. If Delivery doesn't have it, the order fails and
 * the session shows Delivery's error.
 */
export async function deliveryTemplateVersionFor(
    moduleId: string,
    moduleVersionId: string
): Promise<string> {
    const newestSave = (await ModuleService.getModuleHistory(moduleId))
        .filter((save) => save.versionId === moduleVersionId && save.draftVersion)
        .sort((a, b) => Number(b.createdAt) - Number(a.createdAt))[0]
    return newestSave?.draftVersion ?? moduleVersionId
}

export async function deliverySessionRequest({
    moduleId,
    moduleVersionId,
    job,
    prompt,
}: {
    moduleId: string
    moduleVersionId: string
    job: VirtualCandidateJob
    prompt?: string
}): Promise<StartVirtualCandidateSessionRequest> {
    const jobRole = job.jobRole.trim()
    return {
        moduleTemplateVersionId: await deliveryTemplateVersionFor(moduleId, moduleVersionId),
        jobMetadata: {
            requisitionId: job.requisitionId.trim(),
            ...(jobRole ? { jobRole } : {}),
        },
        ...(prompt?.trim() ? { prompt: prompt.trim() } : {}),
    }
}
